import { useRef } from 'react';
import { Box } from '@chakra-ui/react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import type { UseFormReturn } from 'react-hook-form';
import { Text } from '../../ui-components';
import { maxLength, regex } from '../../utils/validation';
import type { FieldConfig } from '../field';

const messages = defineMessages({
    required: { defaultMessage: 'Please enter a {field}', id: 'write-review.error.field.required' },
    requiredEmail: {
        defaultMessage: 'Please enter a valid email address',
        id: 'error.field.email',
    },
    maxLength: {
        defaultMessage: 'No more than {max} characters',
        id: 'error.field.maxLength',
    },
    noSpecialChars: {
        defaultMessage: 'No special characters',
        id: 'error.field.no.special.characters',
    },
    firstName: { defaultMessage: 'First Name', id: 'turn-to.labels.first-name' },
    review: { defaultMessage: 'Review', id: 'turn-to.review-modal.label.review' },
    reviewTitle: { defaultMessage: 'Review title', id: 'turn-to.review-modal.label.review-title' },
    lastName: { defaultMessage: 'Last Name', id: 'turn-to.labels.last-name' },
    email: { defaultMessage: 'Email', id: 'turn-to.labels.email' },
    nickName: { defaultMessage: 'Nickname', id: 'turn-to.labels.nickname' },
    invalidEmail: {
        id: 'footer.email.invalid_email_error',
        defaultMessage: 'Enter a valid email address',
    },
    youtubeLink: {
        id: 'turn-to.errors.youtube',
        defaultMessage: 'Invalid YouTube link',
    },
});

const validateRating = (value: number): boolean => value > 0;

export function useWriteReviewFields(
    form: UseFormReturn<{
        review: string;
        reviewTitle: string;
        rating: number;
        firstName: string;
        lastName: string;
        email: string;
        nickName: string;
        video: string;
        photo: string;
    }>,
): Record<string, FieldConfig> {
    const {
        control,
        formState: { errors },
    } = form;
    const intl = useIntl();

    const fields: Record<string, FieldConfig & { getValue?: () => string }> = {
        globalError: {
            labelRef: useRef<HTMLDivElement>(),
            name: 'globalError',
            label: 'globalError',
            type: 'hidden',
            control,
        },
        review: {
            id: 'turn-to-review',
            name: 'review',
            label: intl.formatMessage(messages.review),
            autoFocus: false,
            helpText: (
                <Box>
                    <Text size="small">
                        <FormattedMessage
                            id="turn-to.review-modal.review-helper"
                            defaultMessage="Make your review great: Describe what you liked, what you didn’t like, and other key things shoppers should know (minimum 10 characters)"
                        />
                    </Text>
                </Box>
            ),
            type: 'textarea',
            rules: {
                required: intl.formatMessage({
                    id: 'turn-to-review-modal.review-length',
                    defaultMessage: 'Your review must be at least 10 characters in length',
                }),
                minLength: {
                    value: 10,
                    message: intl.formatMessage({
                        id: 'turn-to-review-modal.review-length',
                        defaultMessage: 'Your review must be at least 10 characters in length',
                    }),
                },
            },
            error: errors.review,
            control,
        },
        rating: {
            id: 'turn-to.review-modal.review-title',
            name: 'rating',
            control,
            type: 'invisible',
            rules: {
                validate: (value: number) =>
                    validateRating(value) ||
                    intl.formatMessage({
                        defaultMessage: 'Please select an overall rating',
                        id: 'turn-to.review.modal.review-title-helper',
                    }),
            },
            error: errors.rating,
        },
        reviewTitle: {
            labelRef: useRef(),
            id: 'turn-to.review-modal.review-title',
            name: 'reviewTitle',
            label: intl.formatMessage(messages.reviewTitle),
            type: 'text',
            helpText: (
                <Text size="small">
                    <FormattedMessage
                        id="turn-to.review-modal.review-title-helper"
                        defaultMessage="Your overall impression (150 characters or less)"
                    />
                </Text>
            ),
            rules: {
                maxLength: {
                    value: maxLength.reviewTitle,
                    message: intl.formatMessage(messages.maxLength, {
                        max: maxLength.reviewTitle,
                    }),
                },
            },
            error: errors.reviewTitle,
            control,
        },
        photo: {
            type: 'file',
            name: 'photo',
            control: form.control,
            label: intl.formatMessage({
                id: 'turn-to.review-modal.add-photo',
                defaultMessage: 'Add photo',
            }),
            accept: 'image/*',
            multiple: 'multiple',
        },
        video: {
            type: 'text',
            name: 'video',
            control,
            label: intl.formatMessage({
                id: 'turn-to.review-modal.youtube-link',
                defaultMessage: 'Youtube link',
            }),
            helpText: (
                <Box marginTop={2}>
                    <Text size="small">
                        <FormattedMessage
                            id="turn-to.review-modal.youtube-link-helper"
                            defaultMessage="Your video must be a public YouTube video"
                        />
                    </Text>
                </Box>
            ),
            rules: {
                required: intl.formatMessage(messages.youtubeLink),
                pattern: {
                    value: regex.youtube,
                    message: intl.formatMessage(messages.youtubeLink),
                },
            },
            error: errors.video,
        },
        firstName: {
            labelRef: useRef(),
            id: 'turn-to-question-first-name-field',
            name: 'firstName',
            autoComplete: 'given-name',
            label: intl.formatMessage(messages.firstName),
            type: 'text',
            rules: {
                maxLength: {
                    value: maxLength.firstName,
                    message: intl.formatMessage(messages.maxLength, {
                        max: maxLength.firstName,
                    }),
                },
                pattern: {
                    value: regex.firstName,
                    message: intl.formatMessage(messages.noSpecialChars),
                },
                required: intl.formatMessage(messages.required, { field: 'First name' }),
            },
            error: errors.firstName,
            control,
        },
        lastName: {
            labelRef: useRef(),
            id: 'turn-to-question-first-name-field',
            name: 'lastName',
            autoComplete: 'family-name',
            label: intl.formatMessage(messages.lastName),
            type: 'text',
            rules: {
                maxLength: {
                    value: maxLength.lastName,
                    message: intl.formatMessage(messages.maxLength, {
                        max: maxLength.lastName,
                    }),
                },
                pattern: {
                    value: regex.lastName,
                    message: intl.formatMessage(messages.noSpecialChars),
                },
                required: intl.formatMessage(messages.required, { field: 'Last name' }),
            },
            error: errors.lastName,
            control,
        },
        email: {
            labelRef: useRef(),
            id: 'turn-to-question-first-name-field',
            name: 'email',
            autoComplete: 'email',
            label: intl.formatMessage(messages.email),
            type: 'email',
            rules: {
                maxLength: {
                    value: maxLength.email,
                    message: intl.formatMessage(messages.maxLength, {
                        max: maxLength.email,
                    }),
                },
                pattern: {
                    value: regex.email,
                    message: intl.formatMessage(messages.invalidEmail),
                },
                required: intl.formatMessage(messages.requiredEmail),
            },
            error: errors.email,
            control,
        },
        nickName: {
            labelRef: useRef(),
            id: 'turn-to-question-first-name-field',
            name: 'nickName',
            label: intl.formatMessage(messages.nickName),
            type: 'text',
            helpText: (
                <Box>
                    <Text>
                        <FormattedMessage
                            id="turn-to.submit-modal.nickname-helper"
                            defaultMessage="Leave blank to use your first name and last initial"
                        />
                    </Text>
                </Box>
            ),
            rules: {
                maxLength: {
                    value: maxLength.firstName,
                    message: intl.formatMessage(messages.maxLength, {
                        max: maxLength.firstName,
                    }),
                },
                pattern: {
                    value: regex.firstName,
                    message: intl.formatMessage(messages.noSpecialChars),
                },
            },
            error: errors.nickName,
            control,
        },
    };

    return fields;
}
